<template>
    <v-form ref="form">
        <v-card outlined class="mt-4 pa-4 d-flex flex-column g-5" style="gap: 5px;">
            <h2 class="mb-2">Información Básica</h2>
            <v-text-field
                :readonly="readonly"
                dense
                outlined
                label="Nombre de la encuesta"
                placeholder="Ej: Encuesta de Satisfacción 2025"
                persistent-placeholder
                v-model="form.name"
                prepend-inner-icon="mdi-playlist-check"
                :rules="[v => !!v || 'El nombre es requerido']"
                :error-messages="inputErrors.name && inputErrors.name.includes('validation.unique') ? 'El nombre debe ser único' : []"
            ></v-text-field>
            <v-text-field
                :readonly="readonly"
                dense
                outlined
                label="Descripción"
                placeholder="Describe el proposito de esta encuesta..."
                persistent-placeholder
                v-model="form.description"
                prepend-inner-icon="mdi-text-box-outline"
            ></v-text-field>
        </v-card>

        <v-card elevation="0" class="d-flex justify-space-between align-center pa-4 mt-4">
            <h2>Preguntas</h2>
            <v-btn small color="primary" prepend-icon="mdi-plus" @click="addQuestion" :disabled="readonly">
                <v-icon small left>mdi-plus</v-icon>Agregar pregunta
            </v-btn>
        </v-card>
        <div v-if="form.questions.length === 0" class="d-flex flex-column align-center ma-6 pa-6" style="border-style: dotted; border-color: rgba(0, 0, 0, 0.12);">
            <span>No hay preguntas aún.</span>
            <span>Haz clic en "Agregar Pregunta" para comenzar</span>
        </div>
        <template v-else>
            <template v-for="(question, questionIndex) of form.questions">
                <template v-if="question.isOpen">
                    <v-card outlined class="mt-4 pa-4" :key="questionIndex">
                        <div class="d-flex justify-space-between align-center mb-4">
                            <h4>
                                <v-icon @click="question.isOpen = false">mdi-chevron-up</v-icon>
                                Pregunta {{ questionIndex + 1 }}
                            </h4>
                            <v-btn text small color="error" prepend-icon="mdi-delete" @click="removeQuestion(questionIndex)" :disabled="readonly">
                                <v-icon>mdi-delete-outline</v-icon>
                            </v-btn>
                        </div>
                        <v-text-field
                            :readonly="readonly"
                            dense
                            outlined
                            label="Pregunta"
                            placeholder="¿Cuál es tu pregunta?"
                            persistent-placeholder
                            v-model="question.question"
                            prepend-inner-icon="mdi-playlist-check"
                            :rules="[v => !!v || 'La pregunta es requerida']"
                        ></v-text-field>
                        <v-card outlined class="d-flex pa-2 px-4 justify-space-between align-center">
                            <div>
                                <h4>Respuesta Abierta</h4>
                                <span style="opacity: 0.8;">Permite que los usuarios escriban su propia respuesta</span>
                            </div>
                            <v-switch v-model="question.isFreeResponse" :readonly="readonly"></v-switch>
                        </v-card>
                        <template v-if="!question.isFreeResponse">
                            <v-card elevation="0" class="d-flex justify-space-between align-center my-4">
                                <h4>Opciones</h4>
                                <v-btn small color="primary" outlined prepend-icon="mdi-plus" @click="addOption(questionIndex)" :disabled="readonly">
                                    <v-icon small left>mdi-plus</v-icon>Agregar opción
                                </v-btn>
                            </v-card>
                            <div v-for="(option, optionIndex) of question.options" :key="optionIndex" class="d-flex" style="gap: 5px;">
                                <v-avatar color="grey" size="40" class="mt-0" style="border-radius: 2px; opacity: 0.7;">{{ optionIndex + 1 }}</v-avatar>
                                <v-text-field
                                    :readonly="readonly"
                                    dense
                                    outlined
                                    :label="`Opción ${optionIndex + 1}`"
                                    placeholder="Escribe la opción"
                                    persistent-placeholder
                                    v-model="option.value"
                                    prepend-inner-icon="mdi-playlist-check"
                                    :rules="[v => !!v || 'La opción es requerida']"
                                ></v-text-field>    
                                <v-btn text small color="error" class="mt-2" prepend-icon="mdi-delete" @click="removeOption(questionIndex, optionIndex)" :disabled="readonly">
                                    <v-icon>mdi-delete-outline</v-icon>
                                </v-btn>
                            </div>
                        </template>
                    </v-card>
                </template>
                <template v-else>
                    <v-card outlined class="mt-4 pa-4 d-flex align-center justify-space-between" :key="questionIndex">
                        <div class="d-flex align-center" style="gap: 5px;">
                            <v-icon @click="question.isOpen = true">mdi-chevron-down</v-icon>
                            <h4>Pregunta {{ questionIndex + 1 }} <v-icon>mdi-chevron-right</v-icon></h4>
                            <span>"{{ question.question ? question.question : 'Aún no tiene pregunta' }}"</span>
                        </div>
                        <v-btn text small color="error" prepend-icon="mdi-delete" @click="removeQuestion(questionIndex)" :disabled="readonly">
                            <v-icon>mdi-delete-outline</v-icon>
                        </v-btn>
                    </v-card>
                </template>
            </template>
        </template>
        <v-snackbar
            :value="alertError.show"
            :color="alertError.color"
            :timeout="-1"
        >
            {{ alertError.message }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    color="white"
                    text
                    v-bind="attrs"
                    @click="alertError.show = false"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
    </v-form>
</template>

<script>
    import { v4 as uuidv4 } from 'uuid'

    import { RequiredRule } from '@dracul/common-frontend'

    export default {
        name: "SurveysForm",
        data() {
            return {
                uuidv4
            }
        },
        mixins: [RequiredRule],
        props: {
            readonly: {
                type: Boolean,
                default: false
            },
            value: {
                type: Object,
                required: true
            },
            inputErrors: {
                type: Object,
                default: () => ({})
            },
            alertError: {
                type: Object,
                default: () => ({show: false, message: '', color: 'error'})
            }
        },
        computed: {
            form: {
                get() {
                    return this.value
                },
                set(val) {
                    this.$emit('input', val)
                }
            },
            alertSnackbar: {
                get() {
                    return this.alertError.show
                }
            }
        },
        methods: {
            addQuestion() {
                this.form.questions.push({ question: '', options: [], isFreeResponse: false, isOpen: true})
            },
            addOption(questionIndex) {
                this.form.questions[questionIndex].options.push({ value: '' })
            },
            removeQuestion(questionIndex) {
                this.form.questions.splice(questionIndex, 1)
            },
            removeOption(questionIndex, optionIndex) {
                this.form.questions[questionIndex].options.splice(optionIndex, 1)
            },
            validate() {
                return this.$refs.form.validate()
            }
        }
    }
</script>